import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Edit, ClipboardList } from "lucide-react";
import { useModuleQuizzes, useCreateQuiz, useDeleteQuiz, Quiz } from "@/hooks/useQuizzes";
import { QuizBuilder } from "./QuizBuilder";

interface QuizManagerProps {
  moduleId: string;
  moduleName: string;
}

export const QuizManager = ({ moduleId, moduleName }: QuizManagerProps) => {
  const { data: quizzes, isLoading } = useModuleQuizzes(moduleId);
  const createQuiz = useCreateQuiz();
  const deleteQuiz = useDeleteQuiz();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedQuizId, setSelectedQuizId] = useState<string | null>(null);
  const [newQuiz, setNewQuiz] = useState({
    title: "",
    description: "",
    passing_score: 70,
  });

  const handleCreateQuiz = async () => {
    if (!newQuiz.title.trim()) return;

    await createQuiz.mutateAsync({
      module_id: moduleId,
      title: newQuiz.title,
      description: newQuiz.description || undefined,
      passing_score: newQuiz.passing_score,
    });

    setNewQuiz({ title: "", description: "", passing_score: 70 });
    setShowCreateDialog(false);
  };

  if (selectedQuizId) {
    return (
      <QuizBuilder
        quizId={selectedQuizId}
        onBack={() => setSelectedQuizId(null)}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Quizzes</h3>
          <p className="text-sm text-muted-foreground">Module: {moduleName}</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Create Quiz
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Quiz</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Quiz Title</Label>
                <Input
                  value={newQuiz.title}
                  onChange={(e) => setNewQuiz({ ...newQuiz, title: e.target.value })}
                  placeholder="Enter quiz title..."
                />
              </div>
              <div className="space-y-2">
                <Label>Description (optional)</Label>
                <Textarea
                  value={newQuiz.description}
                  onChange={(e) => setNewQuiz({ ...newQuiz, description: e.target.value })}
                  placeholder="Enter quiz description..."
                />
              </div>
              <div className="space-y-2">
                <Label>Passing Score (%)</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={newQuiz.passing_score}
                  onChange={(e) => setNewQuiz({ ...newQuiz, passing_score: parseInt(e.target.value) || 70 })}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateQuiz}
                  disabled={!newQuiz.title.trim() || createQuiz.isPending}
                >
                  Create Quiz
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="text-muted-foreground">Loading quizzes...</div>
      ) : quizzes && quizzes.length > 0 ? (
        <div className="grid gap-3">
          {quizzes.map((quiz) => (
            <QuizCard
              key={quiz.id}
              quiz={quiz}
              onEdit={() => setSelectedQuizId(quiz.id)}
              onDelete={() => deleteQuiz.mutate(quiz.id)}
            />
          ))}
        </div>
      ) : (
        <Card className="border-dashed">
          <CardContent className="py-8 text-center text-muted-foreground">
            <ClipboardList className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No quizzes yet. Create your first quiz for this module.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

interface QuizCardProps {
  quiz: Quiz;
  onEdit: () => void;
  onDelete: () => void;
}

const QuizCard = ({ quiz, onEdit, onDelete }: QuizCardProps) => {
  return (
    <Card className="hover:shadow-sm transition-shadow">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-base font-medium flex items-center gap-2">
              {quiz.title}
              <Badge variant={quiz.is_published ? "default" : "secondary"}>
                {quiz.is_published ? "Published" : "Draft"}
              </Badge>
            </CardTitle>
            {quiz.description && (
              <p className="text-sm text-muted-foreground mt-1">{quiz.description}</p>
            )}
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={onEdit}>
              <Edit className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onDelete}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex gap-4 text-sm text-muted-foreground">
          <span>Passing score: {quiz.passing_score}%</span>
          {quiz.time_limit_minutes && (
            <span>Time limit: {quiz.time_limit_minutes} min</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
